import { CloseButton, Popover, PopoverButton, PopoverPanel } from '@headlessui/react';

import type { SpenderNotification } from '../api/types.ts';
import { useT } from '../i18n/index.ts';
import { messageTime } from '../lib/dates.ts';
import { rs } from '../lib/money.ts';
import { navigate } from '../lib/router.ts';
import { Icon, type IconName } from '../ui/icons.tsx';
import { Num, Spinner } from '../ui/parts.tsx';
import { noteLink } from './notes.ts';
import { useNoteSync } from './noteSync.tsx';

const LOOK: Record<SpenderNotification['kind'], { icon: IconName; tint: string }> = {
  needs_fix: { icon: 'pencil', tint: 'bg-warning-tint text-warning-text' },
  rejected: { icon: 'close', tint: 'bg-danger-tint text-danger-text' },
  cash_in: { icon: 'wallet', tint: 'bg-success-tint text-success-text' },
};

/** Home header bell: owner messages (Needs fix, Rejected) and cash given, with an unread badge. */
export function NotificationBell() {
  const { t, lang } = useT();
  const { notes, error, refresh, markRead, markAllRead } = useNoteSync();
  const unread = notes?.unread ?? 0;

  function open(n: SpenderNotification) {
    if (!n.read) markRead(n.id);
    navigate(noteLink(n));
  }

  return (
    <Popover className="relative">
      <PopoverButton
        aria-label={unread ? t('notes.bellUnread', { count: unread }) : t('notes.title')}
        onClick={refresh}
        className="relative flex size-12 shrink-0 items-center justify-center rounded-[10px] border-[1.5px] border-line-strong bg-paper hover:bg-ground focus:outline-none data-focus:outline-3 data-focus:outline-offset-2 data-focus:outline-ink data-open:bg-ground"
      >
        <Icon name="bell" size={22} />
        {unread > 0 && (
          <span
            aria-hidden="true"
            className="num absolute -top-1.5 -right-1.5 min-w-5 rounded-full bg-danger px-1.5 text-[11px] leading-5 font-bold text-white ring-2 ring-paper rtl:right-auto rtl:-left-1.5"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </PopoverButton>

      <PopoverPanel
        anchor="bottom end"
        transition
        className="z-50 flex max-h-[min(34rem,calc(var(--app-h,100vh)-6rem))] w-[min(26rem,calc(100vw-1rem))] flex-col rounded-xl border-[1.5px] border-line bg-paper shadow-[0_16px_40px_rgba(19,33,31,0.22)] [--anchor-gap:6px] [--anchor-padding:8px] transition duration-100 ease-out data-closed:scale-95 data-closed:opacity-0"
      >
        <div className="flex items-center justify-between gap-2 border-b-[1.5px] border-line py-1.5 ps-4 pe-1.5">
          <span className="flex min-w-0 flex-col leading-tight">
            <strong className="text-[17px] rtl:text-[19px]">{t('notes.title')}</strong>
            {unread > 0 && <span className="text-[13px] text-muted rtl:text-[15px]">{t('notes.unread', { count: unread })}</span>}
          </span>
          {unread > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className="h-12 rounded-lg px-3 text-sm font-semibold text-muted underline-offset-4 hover:bg-ground hover:text-ink hover:underline rtl:text-[15px]"
            >
              {t('notes.markAll')}
            </button>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {!notes && !error && (
            <div className="flex justify-center py-10">
              <Spinner size={26} />
            </div>
          )}
          {!notes && error && <p className="m-0 px-4 py-6 text-center text-[15px] text-muted">{t('notes.failed')}</p>}
          {notes && notes.items.length === 0 && (
            <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
              <span className="flex size-12 items-center justify-center rounded-full bg-ground text-muted">
                <Icon name="bell" size={22} />
              </span>
              <strong className="rtl:text-[17px]">{t('notes.empty')}</strong>
              <span className="text-sm text-muted rtl:text-[15px]">{t('notes.emptyHint')}</span>
            </div>
          )}
          {notes && notes.items.length > 0 && (
            <ul className="m-0 list-none p-1.5">
              {notes.items.map((n) => {
                const look = LOOK[n.kind];
                return (
                  <li key={n.id}>
                    <CloseButton
                      as="button"
                      type="button"
                      onClick={() => open(n)}
                      className={`flex min-h-12 w-full items-start gap-3 rounded-lg px-2.5 py-2.5 text-start hover:bg-ground focus-visible:outline-3 focus-visible:-outline-offset-3 focus-visible:outline-ink ${
                        n.read ? '' : 'bg-brand-tint'
                      }`}
                    >
                      <span className={`mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full ${look.tint}`}>
                        <Icon name={look.icon} size={18} />
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="flex items-baseline justify-between gap-2">
                          <span className={`truncate text-sm rtl:text-[15px] ${n.read ? 'font-semibold text-muted' : 'font-bold'}`}>
                            {!n.read && <span className="sr-only">{t('notes.new')}: </span>}
                            {t(`notes.kind.${n.kind}`)}
                          </span>
                          <span className="num shrink-0 text-xs text-muted">{messageTime(n.createdAt, lang)}</span>
                        </span>
                        <span className="flex items-baseline justify-between gap-2">
                          <span className="truncate text-[15px] font-semibold rtl:text-[17px]">
                            {n.kind === 'cash_in' ? n.entityName : (n.itemName ?? n.entityName)}
                          </span>
                          <Num className="shrink-0 font-bold">{rs(n.amount)}</Num>
                        </span>
                        {n.message && <span className="line-clamp-2 text-[14px] break-words text-ink rtl:text-[16px]">“{n.message}”</span>}
                        <span className="flex min-w-0 items-center gap-1.5 text-[13px] text-muted rtl:text-[15px]">
                          <span className="size-2 shrink-0 rounded-full" style={{ background: n.entityColour }} />
                          <span className="truncate">
                            {n.fromName ? t('notes.from', { name: n.fromName }) : n.entityName}
                            {n.kind !== 'cash_in' && ` · ${n.entityName}`}
                          </span>
                        </span>
                      </span>
                      {!n.read && <span aria-hidden="true" className="mt-2 size-2.5 shrink-0 rounded-full bg-brand" />}
                    </CloseButton>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverPanel>
    </Popover>
  );
}
